import { apiClient as api } from './api';
import { AssetService } from './assetService';
import { LibraryService } from './libraryService';
import { callV3, type ApiResponse } from '../api/envelope';
import type { LibraryItem, LibraryPage } from '../domain';

/**
 * Where an Animation Lab input comes from. Every source the labs take is an owned asset id, which the
 * video operations resolve to a signed link for the provider; this module gets one from a file the
 * person uploads, or from their library — instead of asking them to type an id.
 */

export type MediaKind = 'image' | 'video';

/** A chosen source: the asset id the request sends, and something to show while it is chosen. */
export interface MediaSource {
  assetId: string;
  /** Loadable by an img/video tag (a signed link, or a local preview of an upload). */
  previewUrl: string;
  /** What the person picked: a file name, or a library item's prompt. */
  label: string;
  origin: 'upload' | 'library' | 'handoff';
}

/** Under Cloud Run's 32 MiB request limit, for images and video alike. */
export const MAX_UPLOAD_BYTES = 30 * 1024 * 1024;

export const ACCEPTED_TYPES: Record<MediaKind, readonly string[]> = {
  image: ['image/png', 'image/jpeg', 'image/webp'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
};

/** The `accept` attribute of the file input. */
export const ACCEPT_ATTRIBUTE: Record<MediaKind, string> = {
  image: '.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp',
  video: '.mp4,.webm,.mov,video/mp4,video/webm,video/quicktime',
};

const EXTENSION_TYPES: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp',
  mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
};

/** The file's type: its declared type, or (when the browser gives none) its extension's. */
export function fileTypeOf(file: Pick<File, 'type' | 'name'>): string {
  if (file.type) return file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_TYPES[extension] ?? '';
}

/**
 * Why this file can't be a source of this kind, or null when it can. Checked before uploading, so a
 * wrong file never costs a round trip; the server checks the bytes again either way.
 */
export function validateSourceFile(kind: MediaKind, file: Pick<File, 'type' | 'name' | 'size'>): string | null {
  if (!ACCEPTED_TYPES[kind].includes(fileTypeOf(file))) {
    return kind === 'image'
      ? 'Choose a PNG, JPEG or WebP image.'
      : 'Choose an MP4, WebM or MOV video.';
  }
  if (file.size <= 0) return 'That file is empty.';
  if (file.size > MAX_UPLOAD_BYTES) return `That file is larger than ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB.`;
  return null;
}

interface VideoUploadResult {
  assetId: string;
  url: string;
  contentType: string;
  sizeBytes: number;
}

export const MediaSourceService = {
  /** Upload a file and return it as a source. The preview is the server's signed link when it has one. */
  async upload(kind: MediaKind, file: File): Promise<MediaSource> {
    if (kind === 'image') {
      const uploaded = await AssetService.uploadImage(file);
      return {
        assetId: uploaded.imageId,
        previewUrl: uploaded.signedUrl ?? uploaded.url,
        label: file.name,
        origin: 'upload',
      };
    }
    const form = new FormData();
    form.append('file', file);
    const uploaded = await callV3<VideoUploadResult>(
      api.post<ApiResponse<VideoUploadResult>>('/api/v3/media/videos', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
    return { assetId: String(uploaded.assetId), previewUrl: uploaded.url, label: file.name, origin: 'upload' };
  },

  /** The person's Coral images or videos, newest first — generated, uploaded and imported alike. */
  listLibrary: (kind: MediaKind, page = 1, pageSize = 24): Promise<LibraryPage> =>
    LibraryService.listCoral({ kind, page, pageSize }),
};

/** A library item as a source. */
export function sourceFromLibrary(item: LibraryItem): MediaSource {
  return {
    assetId: item.id,
    previewUrl: item.thumbnailUrl ?? item.url,
    label: item.prompt?.trim() || (item.kind === 'video' ? 'Video from your library' : 'Image from your library'),
    origin: 'library',
  };
}
